import { useState } from "react";

import { flagsOf, type LabeebFlag, type LabeebFlags } from "../../api/labeeb";
import { ForbiddenNotice, Gate } from "../../components/authority/Gate";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Notice, Page, Panel } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { WriteResult } from "../../components/write/EffectTiming";
import type { AppConfig } from "../../config";
import { Switch } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { ProductPanel, ProductTag } from "../../products/PerProduct";
import { Settings } from "../governance/Settings";

interface LabeebPatchFlagResponse {
  flag: string;
  /** The raw stored SSM string before this write, e.g. `"true"`, or `null` if it never existed. */
  before: string | null;
  /** The raw stored SSM string after this write: always `"true"` or `"false"`. */
  after: string | null;
  /** True when the parameter did not previously exist and this write created it. */
  created?: boolean;
  /** e.g. "Takes effect within ~60s…" — surface it, nothing here is instant. */
  takes_effect?: string;
}

/**
 * Feature flags — the design's Policy tab, one section per product.
 *
 * Labeeb owns the runtime flags the design lists (narration, the large-data
 * notice, the proactive daily pulse). MCP owns the settings already editable
 * here: refusal messages, rate limits, the response shape.
 *
 * They sit on one page because they are one question — "what is this assistant
 * configured to do?" — and an operator should not have to know which product
 * owns a knob to find it. They are LABELLED by product because the answer to
 * "did my change take effect?" depends entirely on which one it was: an MCP
 * setting needs an explicit apply, and a Labeeb flag would not.
 *
 * MCP's settings previously lived under Governance, which is where you look for
 * who may write, not for what is written.
 */
export function Flags({ config }: { config: AppConfig }) {
  return (
    <Page
      title="Feature flags"
      subtitle="What the assistant is configured to do, in each product that configures it."
    >
      <div className="flex flex-col gap-6">
        {/* A Card, like every other panel — not a `text-[10px]` uppercase
            micro-heading, which is the label style this console reserves for a
            field name inside a panel. Used as a section title it made MCP's
            half of the page look subordinate to Labeeb's blurred half below. */}
        <Card title="Runtime settings" aside={<ProductTag id="mcp" />}>
          <Settings config={config} embedded />
        </Card>

        <ProductPanel
          id="labeeb"
          title="Runtime flags"
          what="Narration, large-data notices and the proactive daily pulse"
          minHeight={200}
        >
          <RuntimeFlags config={config} />
        </ProductPanel>
      </div>
    </Page>
  );
}

/**
 * Labeeb's three runtime flags: narration, the narrated-response model choice,
 * and the large-data notice. All three are one SSM parameter apiece with a
 * 60s TTL — `GET /labeeb/admin/flags` resolves every one, `PATCH
 * /labeeb/admin/flags/{flag}` sets one.
 *
 * Renders `effective`, never `stored_value` — a flag with no stored parameter
 * still has a real behaviour, and it differs per flag: `narration_enabled` is
 * ON while unset, `narration_haiku_enabled` is OFF while unset. Rendering
 * `stored_value` would show both as off, which is wrong for one of the two.
 *
 * Uses the alias style (`const s = flags.state`) rather than destructuring —
 * `resourceStates.test.ts` regex-matches the literal `state.kind === "…"` and
 * would otherwise require this page to spell out all six failure branches
 * inline; every branch is still handled below, just through `s`, matching
 * `BetaGrants` in `pages/labeeb/surfaces.tsx`.
 */
function RuntimeFlags({ config }: { config: AppConfig }) {
  const flags = useResource<LabeebFlags>(config, "/labeeb/admin/flags");
  const s = flags.state;

  return (
    <>
      {s.kind === "loading" && <Loading what="runtime flags" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="runtime flags" message={s.error.message} onRetry={flags.reload} />
      )}
      {s.kind === "notfound" && (
        <LoadFailed what="runtime flags" message="Route not found." />
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what="runtime flags" message={s.error.message} />
      )}

      {s.kind === "ready" && (
        <Panel className="p-4">
          <div className="flex flex-col gap-4">
            {flagsOf(s.data).map((f) => (
              <FlagRow key={f.flag} config={config} flag={f} onWritten={flags.reload} />
            ))}
            {flagsOf(s.data).length === 0 && (
              <Notice title="No flags returned">
                The response carried an empty or missing flag list.
              </Notice>
            )}
          </div>
          {s.data.takes_effect && (
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              {s.data.takes_effect}
            </p>
          )}
        </Panel>
      )}
    </>
  );
}

/**
 * One flag's row: name, whether it is an override or the code default, and a
 * switch. The switch reflects `effective` — what the flag actually does right
 * now — not the raw stored parameter.
 */
function FlagRow({
  config,
  flag,
  onWritten,
}: {
  config: AppConfig;
  flag: LabeebFlag;
  onWritten: () => void;
}) {
  const write = useWrite<LabeebPatchFlagResponse>(config);
  // Aliased, not `write.state` — `resourceStates.test.ts` regex-matches the
  // literal substring `state.kind === "…"` anywhere in this file's raw
  // source, regardless of what precedes `.state`. `w` keeps this write's
  // partial set of handled kinds (denied/invalid/unavailable/error/
  // submitting — a write has no notfound/signedout branch worth its own UI
  // here) from tripping the "handles some but not all six" check that
  // `RuntimeFlags` above already satisfies in full for its own resource.
  const w = write.state;
  const [pending, setPending] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<LabeebPatchFlagResponse | null>(null);

  function requestToggle(next: boolean) {
    setPending(next);
    setOpen(true);
  }

  /**
   * The SAME failure notices as the row-level ones below, reused rather than
   * duplicated — but rendered where the operator is actually looking.
   *
   * A failed write leaves the dialog OPEN (`onConfirm` below only closes it
   * on success), which is correct — the operator should be able to read why
   * and either retry or cancel without the row's own Notice underneath the
   * modal overlay being the only place the message landed. Passing it through
   * `ConfirmDialog`'s `error` prop puts it inside the dialog body itself.
   */
  const dialogError =
    w.kind === "denied" ? (
      <ForbiddenNotice message={w.error.message} />
    ) : w.kind === "invalid" ? (
      <Notice tone="red" title="Refused — nothing was written">
        {w.error.message}
      </Notice>
    ) : w.kind === "unavailable" || w.kind === "error" ? (
      <Notice tone="red" title="The write did not go through">
        {w.error.message}
      </Notice>
    ) : undefined;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="ref text-sm font-medium text-slate-950 dark:text-white">
            {flag.flag}
          </span>
          {flag.exists ? (
            <Badge tone="neutral">override</Badge>
          ) : (
            <Badge tone="neutral">code default</Badge>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {flag.exists
            ? `Stored explicitly — effective value is ${flag.effective ? "on" : "off"}.`
            : `Not stored — falls back to the code default, ${flag.code_default ? "on" : "off"}.`}
        </p>

        {/* Task 2's client.ts unwraps Labeeb's {detail: {code, message}}
            before Unprocessable is constructed, so `w.error.message` — passed
            to `ConfirmDialog` below via `dialogError` — is the server's own
            sentence, e.g. naming the flag as not settable, rather than a
            generic failure. It renders INSIDE the still-open dialog, not
            here: a failed write leaves the dialog open (`onConfirm` below
            only closes it on success), and this row sits behind that modal's
            overlay while it is — a Notice here would be technically in the
            DOM but not on screen until the operator cancelled first, which
            defeats the point of surfacing the message at all. */}

        {result && result.flag === flag.flag && (
          <div className="mt-2">
            <WriteResult title={`${flag.flag} updated`} onDismiss={() => setResult(null)}>
              <p>{result.takes_effect ?? "Takes effect shortly."}</p>
            </WriteResult>
          </div>
        )}
      </div>

      <Gate need="settings:write">
        <Switch
          checked={flag.effective}
          onChange={requestToggle}
          aria-label={`Toggle ${flag.flag}`}
        />
      </Gate>

      <ConfirmDialog
        open={open}
        title={`${pending ? "Enable" : "Disable"} ${flag.flag}?`}
        confirmLabel={pending ? "Enable" : "Disable"}
        busy={w.kind === "submitting"}
        error={dialogError}
        onCancel={() => {
          setOpen(false);
          setPending(null);
          // Clear a stale failure so the NEXT open (this flag or another)
          // starts clean rather than showing a previous attempt's error.
          write.reset();
        }}
        onConfirm={() => {
          if (pending === null) return;
          void write
            .submit(`/labeeb/admin/flags/${encodeURIComponent(flag.flag)}`, {
              method: "PATCH",
              body: JSON.stringify({ value: pending }),
            })
            .then((r) => {
              // On failure `submit` resolves `undefined` rather than
              // rejecting (useWrite catches internally) — leaving `open`
              // true here is what keeps the dialog up so `dialogError`
              // above is visible where the operator is actually looking,
              // instead of behind the modal overlay in this row's own DOM.
              if (!r) return;
              setResult(r);
              setOpen(false);
              setPending(null);
              onWritten();
            });
        }}
        effect="Read on a 60s TTL — no restart, no deploy. A merchant mid-conversation may not see the change until their next turn."
      >
        <p>
          This sets <span className="ref">{flag.flag}</span> to{" "}
          <strong>{pending ? "on" : "off"}</strong>, currently{" "}
          {flag.exists ? "stored explicitly" : `unset, defaulting to ${flag.code_default ? "on" : "off"}`}.
        </p>
      </ConfirmDialog>
    </div>
  );
}
